/**
 * 2D plan views, one per IfcBuildingStorey, plus the four elevations.
 *
 * This is OBC.Views in the core package. The 2.x `OBF.Plans` component no
 * longer exists — if you find a tutorial using it, that tutorial predates the
 * Fragments 3.0 rewrite and none of it applies. See CODE_RULES.md §12.
 *
 * API verified against @thatopen/components 3.4.8 type definitions.
 */
import * as OBC from "@thatopen/components";
import { CAMERA } from "../core/config";
import { logger } from "../core/logger";
import type { ArchWorld } from "./world";

export interface PlanView {
  readonly id: string;
  readonly label: string;
}

export interface ViewController {
  readonly plans: readonly PlanView[];
  /** Switches to the top-down orthographic view for a storey. */
  open(id: string): void;
  /** Returns to free 3D navigation. */
  close(): void;
  readonly activeId: string | null;
}

export async function createViewController(
  components: OBC.Components,
  world: ArchWorld,
): Promise<ViewController> {
  const views = components.get(OBC.Views);
  views.world = world;
  OBC.Views.defaultRange = CAMERA.PLAN_VIEW_RANGE;

  try {
    await views.createFromIfcStoreys({ modelIds: [/.*/] });
  } catch (cause) {
    logger.error("Could not build storey plan views", cause);
  }

  const plans: PlanView[] = [];
  for (const [id] of views.list) {
    plans.push({ id, label: id });
  }
  plans.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

  let activeId: string | null = null;

  return {
    plans,
    get activeId() {
      return activeId;
    },
    open(id) {
      if (!views.list.has(id)) {
        logger.warn("No such plan view", { id });
        return;
      }
      views.open(id);
      activeId = id;
    },
    close() {
      views.close();
      activeId = null;
    },
  };
}
